// The demo integrator backend: it holds the apps' backend credentials and lists the jurisdictions
// of the user's ACT.SSO session that are enabled there (/apps). The page sends the app the user
// picked from that list, the account id they typed, the browser key's hash and a session id, with
// the SSO token (ssoAuth.js) as Authorization: Bearer.
// It is always on this page's own origin at /demo-api, as a customer's own server would be:
// - deployed (DEV, TEST): the site's server (example/demo-backend) forwards it to that
//   environment's ClientBackendSimulation;
// - npm start: src/setupProxy.js forwards it (DEMO_BACKEND_URL for ClientBackendSimulation).
import { Messages, getAccessToken, getEpoch } from "./ssoAuth";

// Place Test Bet (VerifySession) is for local testing only: deployed builds leave it out
// (the build-time check comes first, so production builds drop it entirely)
export const TEST_BET_ENABLED = process.env.NODE_ENV === "development";

const send = (path, body, accessToken) =>
  fetch(`/demo-api${path}`, {
    method: body ? "POST" : "GET",
    // No cookies: the token goes in Authorization. The portal's cookies are on the parent domain
    // (every environment's SSO tokens), and none of them is the demo backend's business.
    credentials: "omit",
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

/**
 * Calls the demo backend as the signed-in user, with the Auth Portal's token. On a 401 the cookie is
 * read again once: an expired token sends the user to the portal's login (ssoAuth), a token the
 * portal renewed since is tried, and a valid token the backend still turns down is an error here,
 * not a trip to the portal, which wouldn't help. Errors carry `status`, and `stale` when the user
 * changed while the call was in flight (its answer belongs to the previous user).
 */
const call = async (path, body) => {
  const token = getAccessToken();
  const askedIn = getEpoch();
  const stale = () => Object.assign(new Error("signed_out"), { status: 0, stale: true });
  if (!token) throw stale();
  let response = await send(path, body, token);
  if (response.status === 401 && askedIn === getEpoch()) {
    const renewed = getAccessToken();
    if (renewed && renewed !== token && askedIn === getEpoch()) response = await send(path, body, renewed);
  }
  if (askedIn !== getEpoch()) throw stale();

  const json = await response.json().catch(() => ({}));
  if (response.ok) return json;
  if (response.status === 401) throw Object.assign(new Error(Messages.NOT_ACCEPTED), { status: 401, details: json });
  const message =
    response.status === 503 && json.error !== "app_not_configured"
      ? Messages.SSO_UNAVAILABLE
      : json.error || `HTTP ${response.status}`;
  throw Object.assign(new Error(message), { status: response.status, details: json });
};

// The jurisdictions the signed-in user may use (their SSO assignments, enabled in the backend)
export const listDemoApps = () => call("/apps").then((r) => r.apps);

// For StartWebSession({ createSession }): the backend calls CreateSession for that app and account
export const createDemoSession = (appId, accountId, browserKeyHash) =>
  call("/sessions", { appId, accountId, browserKeyHash });

// "Place Test Bet": the backend calls VerifySession and decides
export const placeTestBet = (sessionId) => call("/bets", { sessionId });
